import { useEffect, useState } from "react";
import { Copy, RefreshCw, Inbox, Timer } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const ADJ = ["shadow", "neon", "turbo", "delta", "lunar", "hyper", "alpha", "cyber", "prime", "nova"];
const NOUN = ["falcon", "viper", "otter", "raven", "lynx", "orbit", "comet", "pixel", "vector", "echo"];

function genAddress() {
    const a = ADJ[Math.floor(Math.random() * ADJ.length)];
    const n = NOUN[Math.floor(Math.random() * NOUN.length)];
    const num = Math.floor(Math.random() * 90) + 10;
    return `${a}.${n}${num}@maxthmail.io`;
}

export default function TempMailDemo() {
    const { t } = useLanguage();
    const [address, setAddress] = useState(genAddress);
    const [seconds, setSeconds] = useState(600);
    const [demoMail, setDemoMail] = useState(false);

    useEffect(() => {
        const iv = setInterval(() => {
            setSeconds((s) => (s > 0 ? s - 1 : 600));
        }, 1000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        setDemoMail(false);
        const to = setTimeout(() => setDemoMail(true), 4500);
        return () => clearTimeout(to);
    }, [address]);

    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(address);
            toast.success(t.tempmail.copied);
        } catch {
            toast.error(address);
        }
    };

    return (
        <div
            className="glass card-lift relative overflow-hidden rounded-2xl p-6 sm:p-7"
            data-testid="tempmail-demo"
        >
            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-neon/10 blur-3xl" />

            <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mint">
                    <span className="status-dot inline-block h-2 w-2 rounded-full bg-mint" />
                    {t.tempmail.demoLabel}
                </span>
                <span className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500">
                    <Timer className="h-3.5 w-3.5" />
                    {t.tempmail.autoDestroy} {mm}:{ss}
                </span>
            </div>

            <p className="mt-6 text-xs uppercase tracking-widest text-slate-500">
                {t.tempmail.yourAddress}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <code
                    className="rounded-lg border border-slate-700/80 bg-ink px-3.5 py-2.5 font-mono text-sm text-neon"
                    data-testid="tempmail-address"
                >
                    {address}
                </code>
                <button
                    type="button"
                    onClick={copy}
                    data-testid="tempmail-copy-button"
                    className="flex items-center gap-1.5 rounded-lg bg-neon px-3.5 py-2.5 text-xs font-semibold text-ink transition-colors hover:bg-white"
                >
                    <Copy className="h-3.5 w-3.5" />
                    {t.tempmail.copy}
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setAddress(genAddress());
                        setSeconds(600);
                    }}
                    data-testid="tempmail-regenerate-button"
                    aria-label={t.tempmail.regenerate}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-700 px-3.5 py-2.5 text-xs font-medium text-slate-300 transition-colors hover:border-neon/50 hover:text-neon"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                    {t.tempmail.regenerate}
                </button>
            </div>

            <div className="mt-6 border-t border-slate-800 pt-5">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <Inbox className="h-3.5 w-3.5" />
                    {t.tempmail.inbox}
                </p>
                {demoMail ? (
                    <div className="mt-3 rounded-lg border border-slate-800 bg-ink/70 p-4 transition-opacity duration-700">
                        <p className="text-sm font-medium text-slate-200">{t.tempmail.demoSubject}</p>
                        <p className="mt-1 font-mono text-[11px] text-slate-500">
                            no-reply@maxthsolutions.org
                        </p>
                        <p className="mt-2 text-xs leading-relaxed text-slate-400">
                            {t.tempmail.demoPreview}
                        </p>
                    </div>
                ) : (
                    <p className="mt-3 text-xs text-slate-500">{t.tempmail.emptyInbox}</p>
                )}
            </div>

            <p className="mt-5 font-mono text-[10px] leading-relaxed text-slate-600">
                {t.tempmail.demoNote}
            </p>
        </div>
    );
}
